import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useEmbedContext } from '@/hooks/useEmbedContext';
import { useQuestionStats } from '@/hooks/useQuestions';
import { useFilters } from '@/hooks/useFilters';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/DateRangePicker';
import { StatCard } from '@/components/stats/StatCard';
import { StatusChart } from '@/components/stats/StatusChart';
import { SourceChart } from '@/components/stats/SourceChart';
import { CategoryChart } from '@/components/stats/CategoryChart';
import { TimelineChart } from '@/components/stats/TimelineChart';
import { DoctorLeaderboard } from '@/components/stats/DoctorLeaderboard';

function formatResponseTime(ms?: number): string {
  if (ms == null) return 'N/A';
  const hours = ms / (1000 * 60 * 60);
  if (hours < 1) {
    const minutes = Math.round(ms / (1000 * 60));
    return `${minutes}m`;
  }
  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }
  const days = hours / 24;
  return `${days.toFixed(1)}d`;
}

export function StatsPage() {
  const navigate = useNavigate();
  const { bridge } = useEmbedContext();
  const { filters, setDateRange } = useFilters();
  const { data, isLoading } = useQuestionStats(filters);

  useEffect(() => {
    bridge?.setTitle('Question Statistics');
  }, [bridge]);

  return (
    <div className="space-y-4 p-3 sm:space-y-6 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/questions')}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-2xl font-bold">Statistics</h1>
        </div>
        <div className="w-full sm:w-auto">
          <DateRangePicker
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            onChange={setDateRange}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          title="Total Questions"
          value={data?.total ?? 0}
          isLoading={isLoading}
        />
        <StatCard
          title="Pending"
          value={data?.pendingCount ?? 0}
          isLoading={isLoading}
        />
        <StatCard
          title="Answered"
          value={data?.answeredCount ?? 0}
          isLoading={isLoading}
        />
        <StatCard
          title="Avg Response Time"
          value={formatResponseTime(data?.avgResponseTime)}
          isLoading={isLoading}
        />
      </div>

      {/* Charts Row 1: Status + Source */}
      <div className="grid gap-4 md:grid-cols-2">
        <StatusChart data={data?.byStatus ?? {}} isLoading={isLoading} />
        <SourceChart data={data?.bySource ?? {}} isLoading={isLoading} />
      </div>

      {/* Timeline */}
      <TimelineChart data={data?.timeline ?? []} isLoading={isLoading} />

      {/* Category */}
      <CategoryChart data={data?.byCategory ?? {}} isLoading={isLoading} />

      {/* Doctor Leaderboard */}
      <DoctorLeaderboard data={data?.byDoctor ?? []} isLoading={isLoading} />
    </div>
  );
}
